import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import FormElement from '@nineeng/forms';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { getFormBySlug, updateForm } from 'src/api/admin/forms';
import FormCodeDialog from 'src/components/CodeDialog/FormCodeDialog';
import { correctForm, translateForm } from '@repo/shared/helpers';
import { Form, LanguageCode } from '@repo/shared/domain';
interface FormDetailSearch {
	selectedLanguage?: string;
}

export const Route = createFileRoute('/_admin/system/forms/$id')({
	validateSearch: (search: Record<string, unknown>): FormDetailSearch => ({
		selectedLanguage: search.selectedLanguage as string | undefined,
	}),
	loader: async ({ params }) => {
		const form = await getFormBySlug(params.id);
		return { form: form ? correctForm(form) : undefined };
	},
	component: FormDetailPage,
});

function FormDetailPage() {
	const { id } = Route.useParams();
	const { selectedLanguage } = Route.useSearch();
	const { form: initialForm } = Route.useLoaderData();
	const router = useRouter();
	const queryClient = useQueryClient();

	const { data: form, refetch: refetchForm } = useQuery({
		queryKey: ['form-detail', id],
		queryFn: () => getFormBySlug(id),
		placeholderData: initialForm,
	});

	const previewLanguage = (selectedLanguage || form?.language || 'en') as LanguageCode;
	const previewForm = useMemo(() => {
		if (!form) return null;
		return translateForm(form, [previewLanguage]);
	}, [form, previewLanguage]);

	useBreadcrumbLevel(0, 'System');
	useBreadcrumbLevel(1, 'Forms', '/system/forms');
	useBreadcrumbLevel(2, form?.title || 'Form Details');

	const handleClose = () => {
		router.navigate({ to: '/system/forms' });
	};

	const handleSave = async (data: Form) => {
		try {
			await updateForm(form?.slug || '', correctForm(data));
			refetchForm();
			queryClient.invalidateQueries({ queryKey: ['forms'] });
		} catch (_err) {
			// ignore
		}
	};

	if (!form) {
		return <div className="p-4">Form not found</div>;
	}

	const formKey = `${id}-${previewLanguage}`;

	return (
		<FormCodeDialog
			isOpen={true}
			title={form.title}
			data={form}
			Preview={
				<FormElement
					key={formKey}
					schema={previewForm?.formSchema ?? form.formSchema}
					CustomNavigation={() => null}
					selectedLanguage={previewLanguage}
				/>
			}
			onClose={handleClose}
			onSave={handleSave}
		/>
	);
}
